import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useFlags } from '@atlaskit/flag/use-flags';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import {
	getRegistrationErrorAttributes,
	useManageAttachmentRegistration,
} from '@atlassian/proforma-form-renderer/attachment-registration';
import type { AttachmentUploadEndLifecycleEvent } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import { useRegisterAsTemporaryAttachmentService } from '../../../../services/register-as-temporary-attachment';

import { messages } from './messages';

export const useAttachmentUploadEnd = () => {
	di(useFlags, useIntl, useManageAttachmentRegistration, useRegisterAsTemporaryAttachmentService);

	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetch, error } = useRegisterAsTemporaryAttachmentService();
	const manageAttachmentRegistration = useManageAttachmentRegistration();

	useEffect(() => {
		if (!error) {
			return;
		}

		const showErrorFlag = () => {
			let description = formatMessage(messages.uploadErrorDescription);

			if (error instanceof FetchError && error.status === 400) {
				if (error.message) {
					description = error.message;
				}
			}

			showFlag({
				description,
				title: formatMessage(messages.uploadError),
				appearance: 'error',
			});
		};

		showErrorFlag();
	}, [error, showFlag, formatMessage]);

	return useCallback(
		(uploadEndPayload: AttachmentUploadEndLifecycleEvent) => {
			const fileId = uploadEndPayload.file.identifier.id;

			// The shared hook blocks the field while registering and removes the file on failure; we
			// fire FINISHED/FAILED analytics. Fire-and-forget, as the lifecycle handler slot returns void.
			manageAttachmentRegistration(uploadEndPayload, () => fetch({ fileId }))
				.then(() => {
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS} ${Action.FINISHED}`,
						{ fileId },
					);
				})
				.catch((registrationError: unknown) => {
					// The error flag is surfaced by the effect above which reacts to the service `error` state.
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS} ${Action.FAILED}`,
						{
							fileId,
							failureType: 'attachmentRegistration',
							...getRegistrationErrorAttributes(registrationError),
						},
					);
				});
		},
		[fetch, manageAttachmentRegistration, createAnalyticsEvent],
	);
};
